import React, { useState, useContext } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import { SocketContext } from "../context/SocketContext";
import { FaSignInAlt, FaSpinner } from "react-icons/fa";

const Login = () => {
  const { setAuthFromToken } = useContext(AuthContext);
  const { authenticateSocket } = useContext(SocketContext);
  const {
    instanceId,
    conditionId,
    isInstanceReady,
    participantId,
    updateInstance,
    updateInstanceStatus,
  } = useContext(SurveyContext);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isReturningUser = searchParams.get("returning") === "true";
  const urlConditionId = searchParams.get("c");

  // Use URL conditionId if available (for returning users), otherwise use context
  const effectiveConditionId = urlConditionId || conditionId;

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsSubmitting(true);

    try {
      // For returning users, we may not have instanceId - that's okay
      // The backend will find the user by username and condition
      if (!instanceId && !effectiveConditionId) {
        setError("No session found. Please start from the beginning.");
        setIsSubmitting(false);
        return;
      }

      const loginData = {
        username,
        password,
      };

      // Include instanceId if we have it, otherwise include conditionId
      if (instanceId) {
        loginData.instanceId = instanceId;
      } else if (effectiveConditionId) {
        loginData.conditionId = effectiveConditionId;
      }

      const response = await api.post("/auth/login", loginData);

      // Update SurveyContext with the user's actual instance
      console.log("[Login] Setting instance from login response:", response.data.instanceId);
      updateInstance({
        _id: response.data.instanceId,
        conditionId: response.data.condition,
        status: "active",
      });
      updateInstanceStatus("active");

      await setAuthFromToken(response.data.token, {
        userId: response.data.userId,
        username: response.data.username,
        instanceId: response.data.instanceId,
        condition: response.data.condition,
        caseNumber: response.data.caseNumber,
      });

      // Notify socket that this is an active human user login
      // This triggers orchestrator to activate agent behavior
      // Pass token explicitly since React state may not have updated yet
      if (authenticateSocket) {
        authenticateSocket(response.data.token);
      }

      navigate("/newsfeed");
    } catch (error) {
      if (error.response && error.response.data) {
        setError(error.response.data.error || "Login failed");
      } else {
        setError("An unexpected error occurred");
      }
      console.error("Error logging in:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if instance is ready - but allow returning users to login without instance
  if (!isReturningUser && !effectiveConditionId && (!instanceId || !isInstanceReady)) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-spiders-bg">
        <div className="spiders-card p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-spiders-primary-950 flex items-center justify-center">
            <FaSpinner className="w-8 h-8 text-spiders-primary-400 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-spiders-text-primary mb-4">Session Not Ready</h1>
          <p className="text-spiders-text-secondary mb-6">
            Your session is still being prepared. Please wait...
          </p>
          <Link to="/" className="text-spiders-primary-400 hover:text-spiders-primary-300 transition-colors">
            Go back to start
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center items-center min-h-screen bg-spiders-bg">
      <div className="spiders-card p-8 w-full max-w-md relative overflow-hidden">
        {/* Decorative gradient */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-spiders-primary-500 via-spiders-accent-400 to-spiders-primary-500"></div>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-full bg-spiders-primary-950 flex items-center justify-center">
            <FaSignInAlt className="w-5 h-5 text-spiders-primary-400" />
          </div>
          <h1 className="text-2xl font-bold text-spiders-text-primary">Welcome Back</h1>
        </div>
        <p className="text-spiders-text-secondary text-sm mb-6 ml-13">
          Login to continue the discussion
        </p>

        {error && (
          <div className="bg-spiders-error/20 border border-spiders-error text-spiders-error p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-spiders-text-primary text-sm font-medium mb-2" htmlFor="username">
              Username
            </label>
            <input
              type="text"
              id="username"
              className="spiders-input w-full"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-spiders-text-primary text-sm font-medium mb-2" htmlFor="password">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="spiders-input w-full"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
              isSubmitting
                ? "bg-spiders-bg-elevated text-spiders-text-muted cursor-not-allowed"
                : "spiders-btn-primary"
            }`}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="animate-spin" />
                Logging in...
              </>
            ) : (
              "Login"
            )}
          </button>
        </form>

        <p className="mt-6 text-spiders-text-secondary text-center text-sm">
          Don't have an account?
          <Link to="/register" className="text-spiders-primary-400 hover:text-spiders-primary-300 ml-1 transition-colors">
            Register here
          </Link>
        </p>

        {/* Session Info (for debugging, can be removed in production) */}
        {process.env.NODE_ENV === "development" && (
          <div className="mt-6 p-3 bg-spiders-bg-dark rounded-lg text-xs text-spiders-text-muted border border-spiders-border">
            <p>Instance: {instanceId?.slice(0, 8)}...</p>
            <p>Condition: {conditionId}</p>
            {participantId && <p>Participant: {participantId}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
